<template>
  <article
    class="privacy-policy"
    :class="{ 'privacy-policy--dark': $vuetify.theme.dark }"
    lang="en"
    aria-labelledby="privacy-policy-title"
  >
    <div class="policy-layout">
      <aside class="policy-index">
        <details :open="$vuetify.breakpoint.mdAndUp">
          <summary>On this page</summary>
          <nav aria-label="Privacy Policy sections">
            <a
              v-for="section in content.sections"
              :key="section.id"
              :href="`#${section.id}`"
            >{{ section.title }}</a>
            <a href="#privacy-contact">Contact us</a>
          </nav>
        </details>
      </aside>
      <div class="policy-body">
        <header>
          <p class="policy-eyebrow">
            Your history. Your choices.
          </p>
          <h1 id="privacy-policy-title">
            Privacy Policy
          </h1>
          <p class="policy-lead">
            {{ content.intro[0] }}
          </p>
          <p
            v-for="paragraph in content.intro.slice(1, 3)"
            :key="paragraph"
          >
            {{ paragraph }}
          </p>
          <p class="policy-emphasis">
            <strong>{{ content.intro[3] }}</strong>
          </p>
          <a
            class="policy-read-more"
            href="#full-policy"
          >Read the full policy ↓</a>
        </header>
        <div
          id="full-policy"
          class="policy-metadata"
        >
          <p v-if="content.updatedDate">
            Last updated: <time
              :datetime="content.updatedDate"
            >{{ updatedLabel }}</time>
          </p>
          <router-link to="/pages/company">
            Digital Fusion Inc.
          </router-link>
        </div>
        <section
          v-for="section in content.sections"
          :key="section.id"
          class="policy-section"
          :aria-labelledby="section.id"
        >
          <h2 :id="section.id">
            {{ section.title }}
          </h2>
          <template v-for="(block, index) in section.blocks">
            <p
              v-if="block.type === 'paragraph'"
              :key="`${section.id}-${index}`"
            >
              {{ block.text }}
            </p>
            <ul
              v-else
              :key="`${section.id}-${index}`"
            >
              <li
                v-for="item in block.items"
                :key="item"
              >
                {{ item }}
              </li>
            </ul>
          </template>
        </section>
        <section
          class="policy-section policy-contact"
          aria-labelledby="privacy-contact"
        >
          <h2 id="privacy-contact">
            Contact us
          </h2>
          <p><strong>{{ content.contact.operator }}</strong></p>
          <dl>
            <dt v-if="content.contact.email">
              Privacy inquiries
            </dt>
            <dd v-if="content.contact.email">
              <a
                :href="`mailto:${content.contact.email}`"
              >{{ content.contact.email }}</a>
            </dd>
            <dt>Support</dt>
            <dd>
              <router-link :to="content.contact.support">
                Contact Self Biography Support →
              </router-link>
            </dd>
          </dl>
          <p>{{ content.contact.text }}</p>
        </section>
        <footer class="policy-closing">
          <p><strong>{{ content.closing }}</strong></p>
          <a href="#privacy-policy-title">Back to top ↑</a>
        </footer>
      </div>
    </div>
  </article>
</template>
<script>
import content from '@/content/selfBiographyPrivacyPolicy.json'

export default {
  name: 'SelfBiographyPrivacyPolicy',
  data: () => ({ content, previousTitle: '' }),
  computed: {
    updatedLabel() {
      return this.content.updatedDate ? new Date(`${this.content.updatedDate}T12:00:00Z`).toLocaleDateString('en-US', {
        year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC',
      }) : ''
    },
  },
  mounted() {
    this.previousTitle = document.title
    document.title = 'Privacy Policy | Self Biography'
    this.$nextTick(() => {
      const id = this.$route.hash.slice(1)
      const valid = ['privacy-policy-title', 'full-policy', 'privacy-contact', ...this.content.sections.map(section => section.id)]
      const target = valid.includes(id) && document.getElementById(id)
      if (target) target.scrollIntoView()
      else window.scrollTo(0, 0)
    })
  },
  beforeDestroy() { document.title = this.previousTitle },
}
</script>
<style lang="scss" scoped>
.privacy-policy {
  --policy-text: #5e5669; --policy-heading: #393345; --policy-accent: #713ac7; --policy-surface: #fff; --policy-tint: #f3eefb; --policy-divider: rgba(94,86,105,.16);
  max-width: 1160px; margin: 24px auto 48px; color: var(--policy-text); font-size: 16px; line-height: 1.8; text-align: left;
  &--dark { --policy-text: #d0cadf; --policy-heading: #eee9fa; --policy-accent: #c6a5ff; --policy-surface: #312d4b; --policy-tint: #39304f; --policy-divider: rgba(231,227,252,.16); }
  h1,h2 { color: var(--policy-heading); font-weight: 600; line-height: 1.3; text-align: left; scroll-margin-top: 88px; }
  h1 { font-size: 16px; letter-spacing: -.035em; margin-bottom: 24px; }
  h2 { font-size: 16px; letter-spacing: -.02em; margin-bottom: 18px; }
  p { margin-bottom: 18px; }
  strong { font-weight: 600; }
  a { color: var(--policy-accent); text-underline-offset: 4px; }
  a:focus-visible, summary:focus-visible { outline: 2px solid var(--policy-accent); outline-offset: 4px; }
  .policy-eyebrow { color: var(--policy-accent); font-size: .75rem; font-weight: 600; letter-spacing: .08em; text-transform: uppercase; margin-bottom: 14px; }
}
.policy-layout { display: grid; grid-template-columns: 220px minmax(0,1fr); gap: 32px; align-items: start; }
.policy-index { position: sticky; top: 88px; max-height: calc(100vh - 112px); overflow-y: auto; padding-right: 12px; }
.policy-index summary { font-weight: 600; cursor: pointer; min-height: 44px; }
.policy-index nav { display: flex; flex-direction: column; border-left: 1px solid var(--policy-divider); padding-left: 16px; }
.policy-index a { display: flex; align-items: center; min-height: 44px; padding: 6px 0; font-size: .875rem; line-height: 1.5; text-decoration: none; }
.policy-index a:hover { text-decoration: underline; }
.policy-body { min-width: 0; background: var(--policy-surface); border: 1px solid var(--policy-divider); border-radius: 8px; padding: 40px; }
.policy-lead { font-size: 16px; color: var(--policy-heading); }
.policy-emphasis { color: var(--policy-accent); }
.policy-read-more { display: inline-flex; align-items: center; min-height: 44px; font-weight: 600; }
.policy-metadata { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 24px; border-top: 1px solid var(--policy-divider); margin-top: 28px; padding-top: 24px; font-size: .875rem; scroll-margin-top: 88px; }
.policy-metadata p { margin: 0; }
.policy-section { margin-top: 32px; padding-top: 28px; border-top: 1px solid var(--policy-divider); }
.policy-section > :last-child { margin-bottom: 0; }
.policy-section ul { padding-left: 24px; margin-bottom: 18px; }
.policy-section li { padding-left: 4px; margin-bottom: 10px; }
.policy-contact dl { display: grid; grid-template-columns: 150px minmax(0,1fr); gap: 10px 20px; margin: 20px 0; }
.policy-contact dt { font-weight: 600; color: var(--policy-heading); }
.policy-contact dd { overflow-wrap: anywhere; }
.policy-closing { margin-top: 32px; padding-top: 26px; border-top: 1px solid var(--policy-divider); }
.policy-closing > p { color: var(--policy-accent); font-size: 16px; }
.policy-closing > a { display: inline-flex; align-items: center; min-height: 44px; font-size: .875rem; }
@media(max-width:959px) {
  .policy-layout { grid-template-columns: minmax(0,1fr); gap: 20px; }
  .policy-index { position: static; max-height: none; padding-right: 0; }
  .policy-index summary { padding: 8px 0; }
  .policy-body { padding: 28px; }
}
@media(max-width:599px) {
  .privacy-policy { margin: 0 auto 32px; }
  .policy-body { padding: 24px 0 0; border: 0; border-top: 1px solid var(--policy-divider); border-radius: 0; background: transparent; }
  .policy-contact dl { grid-template-columns: minmax(0,1fr); gap: 4px; }
  .policy-contact dd { margin-bottom: 12px; }
  .policy-draft { padding: 16px; }
}
</style>
